import { createBrowserRouter, redirect, type LoaderFunctionArgs } from 'react-router';
import { articleDraftQuery } from './features/articles/api/articles';
import { meQuery } from './features/authentication/api/auth';
import { questionDraftQuery } from './features/questions/api/questions';
import { quizSetQuery } from './features/quizzes/api/quiz';
import { ApiError } from './api/http';
import { queryClient } from './lib/query-client';
import { getRememberedRecoveryUser } from './features/articles/api/draft-recovery';
import { RouterErrorBoundary, WorkspaceRouterLayout } from './components/RouterLayout';

async function requireAdmin({ request }: LoaderFunctionArgs) {
  try {
    const profile = await queryClient.fetchQuery(meQuery);
    if (profile.role !== 'Admin') {
      window.location.assign('/');
      return profile;
    }
    const current = new URL(request.url);
    if (profile.twoFactorRequired && !current.pathname.endsWith('/security/two-factor')) {
      throw redirect('/security/two-factor');
    }
    return profile;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const current = new URL(request.url);
      const returnTo = `${current.pathname}${current.search}`;
      throw redirect(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }

    const cachedProfile = queryClient.getQueryData(meQuery.queryKey);
    const rememberedUserId = getRememberedRecoveryUser();
    if (!navigator.onLine && (cachedProfile || rememberedUserId)) {
      return (
        cachedProfile ?? {
          id: rememberedUserId,
          displayName: 'Administrator',
          role: 'Admin',
        }
      );
    }

    throw error;
  }
}

async function loadArticleDraft(args: LoaderFunctionArgs) {
  await requireAdmin(args);
  const { params } = args;
  if (!params.id) return null;
  try {
    return await queryClient.ensureQueryData(articleDraftQuery(params.id));
  } catch (error) {
    if (!navigator.onLine) return null;
    throw error;
  }
}

async function loadQuestionDraft(args: LoaderFunctionArgs) {
  await requireAdmin(args);
  const { params } = args;
  if (!params.id) return null;
  return queryClient.ensureQueryData(questionDraftQuery(params.id));
}

async function loadQuizSet(args: LoaderFunctionArgs) {
  await requireAdmin(args);
  const { params } = args;
  if (!params.id) return null;
  return queryClient.ensureQueryData(quizSetQuery(params.id));
}

export const router = createBrowserRouter(
  [
    {
      Component: WorkspaceRouterLayout,
      ErrorBoundary: RouterErrorBoundary,
      children: [
        {
          path: '/login',
          lazy: async () => {
            const route = await import('./features/authentication/routes/login');
            return { Component: route.LoginPage };
          },
        },
        {
          path: '/users',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/authentication/routes/users')).UsersPage,
          }),
        },
        {
          path: '/security/two-factor',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/authentication/routes/two-factor')).TwoFactorPage,
          }),
        },
        {
          path: '/account',
          loader: ({ request }) => {
            const current = new URL(request.url);
            const destination =
              current.searchParams.get('flow') === 'register'
                ? '/account/register'
                : current.searchParams.get('flow') === 'forgot-password'
                  ? '/account/forgot-password'
                  : '/account/login';
            window.location.assign(destination);
            return null;
          },
        },
        {
          path: '/verify-email',
          loader: ({ request }) => {
            const current = new URL(request.url);
            window.location.assign(`/account/verify-email${current.search}`);
            return null;
          },
        },
        {
          path: '/reset-password',
          lazy: async () => ({
            Component: (await import('./features/authentication/routes/token-action'))
              .TokenActionPage,
          }),
        },
        {
          path: '/change-password',
          lazy: async () => ({
            Component: (await import('./features/authentication/routes/change-password'))
              .ChangePasswordPage,
          }),
        },
        {
          path: '/',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/workspace/routes/workspace');
            return { Component: route.WorkspacePage, ErrorBoundary: route.WorkspaceErrorBoundary };
          },
        },
        {
          path: 'articles',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/articles/routes/articles');
            return { Component: route.ArticleListPage };
          },
        },
        {
          path: 'articles/new',
          loader: loadArticleDraft,
          lazy: async () => {
            const route = await import('./features/articles/routes/article-editor');
            return { Component: route.ArticleEditorPage };
          },
        },
        {
          path: 'articles/:id',
          loader: loadArticleDraft,
          lazy: async () => {
            const route = await import('./features/articles/routes/article-editor');
            return { Component: route.ArticleEditorPage };
          },
        },
        {
          path: 'articles/:id/preview',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/articles/routes/article-preview');
            return { Component: route.ArticlePreviewPage };
          },
        },
        {
          path: 'media',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/media/routes/media-library');
            return { Component: route.MediaLibraryPage };
          },
        },
        {
          path: 'tags',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/tags/routes/tag-management')).TagManagementPage,
          }),
        },
        {
          path: 'series',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/articles/routes/series-management'))
              .SeriesManagementPage,
          }),
        },
        {
          path: 'questions',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/questions/routes/questions');
            return { Component: route.QuestionListPage };
          },
        },
        {
          path: 'questions/new',
          loader: loadQuestionDraft,
          lazy: async () => {
            const route = await import('./features/questions/routes/question-editor');
            return { Component: route.QuestionEditorPage };
          },
        },
        {
          path: 'questions/import',
          loader: requireAdmin,
          lazy: async () => {
            const route = await import('./features/questions/routes/question-import');
            return { Component: route.QuestionImportPage };
          },
        },
        {
          path: 'questions/:id',
          loader: loadQuestionDraft,
          lazy: async () => {
            const route = await import('./features/questions/routes/question-editor');
            return { Component: route.QuestionEditorPage };
          },
        },
        {
          path: 'index',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/indexing/routes/index-page')).IndexPage,
          }),
        },
        {
          path: 'insights',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/insights/routes/insights-overview'))
              .InsightsOverviewPage,
          }),
        },
        {
          path: 'settings',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/settings/routes/settings-page')).SettingsPage,
          }),
        },
        {
          path: 'audit',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/audit/routes/audit-page')).AuditPage,
          }),
        },
        {
          path: 'insights/questions',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/insights/routes/insights-questions'))
              .InsightsQuestionsPage,
          }),
        },
        {
          path: 'insights/tags',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/insights/routes/insights-tags')).InsightsTagsPage,
          }),
        },
        {
          path: 'insights/searches',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/insights/searches/routes/search-insights-page'))
              .SearchInsightsPage,
          }),
        },
        {
          path: 'insights/opportunities',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/insights/opportunities/routes/opportunities-page'))
              .OpportunitiesPage,
          }),
        },
        {
          path: 'quiz-sets',
          loader: requireAdmin,
          lazy: async () => ({
            Component: (await import('./features/quizzes/routes/quiz-sets')).QuizSetListPage,
          }),
        },
        {
          path: 'quiz-sets/new',
          loader: loadQuizSet,
          lazy: async () => ({
            Component: (await import('./features/quizzes/routes/quiz-set-editor'))
              .QuizSetEditorPage,
          }),
        },
        {
          path: 'quiz-sets/:id',
          loader: loadQuizSet,
          lazy: async () => ({
            Component: (await import('./features/quizzes/routes/quiz-set-editor'))
              .QuizSetEditorPage,
          }),
        },
        {
          path: '*',
          lazy: async () => {
            const route = await import('./routes/not-found');
            return { Component: route.NotFoundPage };
          },
        },
      ],
    },
  ],
  { basename: '/admin' },
);
